/**
 * Trigger kinds whose bound flow can have its input schema stamped from the
 * record screen. Mirrors the backend `/webhook/{id}/stamp_input_schema`
 * endpoint (resources/webhook.py, resources/trigger_input_schema.py).
 *
 * NOTE: only `webhook` — a webhook has a fixed platform trigger envelope. There
 * is deliberately NO schedule stamp endpoint (a scheduled flow's inputs are
 * defined by its scheduler, not by a platform-standard envelope; see
 * resources/schedule.py). Schedule-input-schema stamping is delivered by the
 * Scheduling bundle's schedule-scoped "Stamp input schema on flow" plugin
 * action instead.
 */
export type TriggerKind = "webhook";

/** Response of POST /webhook/{id}/stamp_input_schema. */
export interface TriggerStampResult {
    trigger_kind: TriggerKind;
    trigger_id: string;
    /** The trigger record's name, or null when it has none. */
    trigger_name: string | null;
    flow_id: string;
    flow_name: string;
    current_input_schema: Record<string, unknown> | null;
    computed_input_schema: Record<string, unknown> | null;
    changed: boolean;
    applied: boolean;
}

/**
 * Pretty-print a JSON schema for the current-vs-new confirm panes. An absent or
 * empty schema renders as the placeholder "(empty)" so the diff reads clearly.
 * Pure so it can be unit-tested and shared by the button component.
 */
export const formatSchema = (schema: Record<string, unknown> | null | undefined): string =>
    schema && Object.keys(schema).length ? JSON.stringify(schema, null, 2) : "(empty)";

/**
 * REST path of the stamp endpoint for a trigger record. `by=id` selects the
 * record by its id (the record screen always holds the saved id). The POST body
 * carries `{ apply: false }` for the dry run and `{ apply: true }` to write.
 * Pure so the exact URL contract is unit-tested.
 */
export const triggerStampEndpoint = (triggerKind: TriggerKind, triggerId: string): string =>
    `/${triggerKind}/${triggerId}/stamp_input_schema?by=id`;
